// Considered: GlobalSearchResultsListMolecule — rejected: item type is `SearchResultSphere`
// (sphere-shaped), renders `SphereRowMolecule` unconditionally, hardcodes English strings and
// imports `primereact/progressspinner` directly. The command bar needs a generic
// label/icon/description row across three unrelated groups (Templates / Create / Builders),
// so generifying that molecule would drag two pre-existing standards violations into this
// spec's diff instead of fixing them on their own terms. See spec Reuse Audit (studio-desk.md).
import React from 'react';
import { useTranslation } from 'localization';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { GlyphColor } from '../../../types/glyph';
import './CommandResultsListMolecule.scss';

export interface CommandResultItem {
  id: string;
  label: string;
  description?: string;
  icon?: string | null;
  glyphColor?: GlyphColor;
  /** Caller-provided trailing slot (e.g. a type chip), mirrors SphereRowMolecule's `trailing`. */
  trailing?: React.ReactNode;
}

export interface CommandResultGroup {
  /** Already-localized group heading (e.g. `t('studio.command.group.templates')`). */
  label: string;
  items: CommandResultItem[];
}

export interface CommandResultsListMoleculeProps {
  groups: CommandResultGroup[];
  /** Id of the currently roving-focused item, across all groups. */
  focusedId?: string | null;
  onSelect: (item: CommandResultItem) => void;
  onFocus?: (item: CommandResultItem) => void;
  /** Override the default "no results" message. */
  emptyMessage?: string;
  className?: string;
}

/**
 * CommandResultsListMolecule - generic grouped, keyboard-navigable result list.
 * Mirrors SphereRowMolecule's roving-selection prop contract (`focused`/`onSelect`/
 * `trailing`) so the codebase keeps one keyboard-list model across command surfaces.
 */
const CommandResultsListMolecule: React.FC<CommandResultsListMoleculeProps> = ({
  groups,
  focusedId = null,
  onSelect,
  onFocus,
  emptyMessage,
  className = '',
}) => {
  const { t } = useTranslation();

  const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);

  if (totalItems === 0) {
    return (
      <div className={`command-results-list-molecule command-results-list-molecule--empty ${className}`}>
        {emptyMessage ?? t('studio.command.empty')}
      </div>
    );
  }

  const handleKeyDown = (item: CommandResultItem) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item);
    }
  };

  return (
    <div className={`command-results-list-molecule ${className}`} role="listbox" aria-label={t('composer.commandResults.ariaLabel')}>
      {groups
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <div key={group.label} className="command-results-list-molecule__group">
            <div className="command-results-list-molecule__group-label">{group.label}</div>
            {group.items.map((item) => {
              const focused = item.id === focusedId;
              return (
                <div
                  key={item.id}
                  role="option"
                  aria-selected={focused}
                  tabIndex={-1}
                  className={`command-results-list-molecule__item${focused ? ' command-results-list-molecule__item--focused' : ''}`}
                  onClick={() => onSelect(item)}
                  onKeyDown={handleKeyDown(item)}
                  onFocus={() => onFocus?.(item)}
                  data-testid="command-results-list-item"
                >
                  <GlyphAtom glyph={item.icon} glyphColor={item.glyphColor} fallbackText={item.label} />
                  <div className="command-results-list-molecule__item-text">
                    <span className="command-results-list-molecule__item-label">{item.label}</span>
                    {item.description && (
                      <span className="command-results-list-molecule__item-description">{item.description}</span>
                    )}
                  </div>
                  {item.trailing !== undefined && (
                    <span className="command-results-list-molecule__item-trailing">{item.trailing}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
};

export default CommandResultsListMolecule;
